import { ExportHelper } from './ExportHelper'
import ReactGA from 'react-ga4'
import {
  barOptions,
  boxplotOptions,
  columnOptions,
  genericOptions,
  lineOptions,
  scatterplotOptions,
  variwideOptions,
} from './ChartTypesDefaultOptions'
import { IChart, Filter, DataType, Column, Properties } from '@services/types/IChart'
import { capitalizeFirstLetter } from './CommonHelper'
import { IDataType } from '@services/types/IDataType'
import Highcharts, {
  Options as ChartOptions,
  Point,
  AxisLabelsFormatterContextObject,
} from 'highcharts'
import ChartTypes from '../data/enums/ChartEnums/ChartTypes'

const gaRegisterEvent = ({
  chartId,
  filters,
}: {
  chartId: string
  filters: Filter[]
}) => {
  ReactGA.event({
    category: 'download',
    action: 'download',
    label: `${chartId}/${filters}`,
  })
}

const DATA_TYPES = new Map<number, IDataType>([
  [0, {
    name: 'Na',
    suffix: '',
    decimalPoints: -1
  }],
  [1, {
    name: 'Mix',
    suffix: '',
    decimalPoints: -1
  }],
  [2, {
    name: 'String',
    suffix: '',
    decimalPoints: -1
  }],
  [3, {
    name: 'Boolean',
    suffix: '',
    decimalPoints: -1
  }],
  [4, {
    name: 'Double',
    suffix: '',
    decimalPoints: -1
  }],
  [5, {
    name: 'Number0',
    suffix: '',
    decimalPoints: 0
  }],
  [6, {
    name: 'Number1',
    suffix: '',
    decimalPoints: 1
  }],
  [7, {
    name: 'Number2',
    suffix: '',
    decimalPoints: 2
  }],
  [8, {
    name: 'Percent0',
    suffix: '%',
    decimalPoints: 0
  }],
  [9, {
    name: 'Percent1',
    suffix: '%',
    decimalPoints: 1
  }],
  [10, {
    name: 'Percent2',
    suffix: '%',
    decimalPoints: 2
  }],
  [11, {
    name: 'Bps0',
    suffix: 'bps',
    decimalPoints: 0
  }],
  [12, {
    name: 'Bps1',
    suffix: 'bps',
    decimalPoints: 1
  }],
  [13, {
    name: 'Bps2',
    suffix: 'bps',
    decimalPoints: 2
  }],
  [14, {
    name: 'Yes',
    suffix: '',
    decimalPoints: -1
  }],
  [15, {
    name: 'YesNo',
    suffix: '',
    decimalPoints: -1
  }],
  [16, {
    name: 'YesNoNa',
    suffix: '',
    decimalPoints: -1
  }],
  [17, {
    name: 'YesNoSome',
    suffix: '',
    decimalPoints: -1
  }],
  [18, {
    name: 'YesNoSomeNa',
    suffix: '',
    decimalPoints: -1
  }],
  [19, {
    name: 'RecordingLiveBothNa',
    suffix: '',
    decimalPoints: -1
  }],
  [20, {
    name: 'GrossNetBoth',
    suffix: '',
    decimalPoints: -1
  }],
  [21, {
    name: 'MonthlyAnnualBoth',
    suffix: '',
    decimalPoints: -1
  }],
  [22, {
    name: 'EstimateFinal',
    suffix: '',
    decimalPoints: -1
  }],
  [23, {
    name: 'EachScenarioSingleEstimate',
    suffix: '',
    decimalPoints: -1
  }],
])

const YOU_COLOR = 'orange'
const GROUP_COLOR = '#2caffe'
const LINE_COLOR = 'black'

type SeriesDataType = number | { name: string; y: number; color: string }

interface ChartActionsProps {
  chartElement: any
  title: string
  caption: { title: string; text: string }
  filters: Filter[]
  id: string
}

/**
 * Generate chart actions for export options.
 */
export const getChartActions = ({
  chartElement,
  title,
  caption,
  filters,
  id,
}: ChartActionsProps) => {
  const exportHelper = new ExportHelper()
  exportHelper.setCharts([
    { chart: chartElement?.chart ?? chartElement, title, caption, filters, id },
  ])
  exportHelper.setFilters(filters)
  exportHelper.setReportTitle(title)

  return [
    {
      id: '1',
      label: 'Export as .PDF',
      action: async () => {
        await exportHelper.setType('pdf').export()
        gaRegisterEvent({ chartId: id, filters })
      },
      value: 'pdf',
    },
    {
      id: '2',
      label: 'Export to Powerpoint',
      action: async () => {
        await exportHelper.setType('pptx').export()
        gaRegisterEvent({ chartId: id, filters })
      },
      value: 'pptx',
    },
    {
      id: '4',
      label: 'Export as Image',
      action: async () => {
        await exportHelper.setType('png').export()
        gaRegisterEvent({ chartId: id, filters })
      },
      value: 'png',
    },
  ]
}

// Utility Functions for Chart Enhancements

const formatYAxisLabels = (chartOptions) => ({
  ...chartOptions,
  yAxis: {
    ...chartOptions.yAxis,
    labels: {
      ...chartOptions.yAxis?.labels,
      formatter: function () {
        const range = this.axis.max - this.axis.min
        const decimals = range < 5 ? 2 : 0
        return Highcharts.numberFormat(this.value, decimals, '.', ',')
      },
    },
  },
})

const setPlotPadding = (chartOptions) => {
  const numCategories = chartOptions?.xAxis?.categories?.length || 0
  const pointWidth = numCategories > 50 ? 5 : numCategories > 30 ? 10 : numCategories > 10 ? 15 : 30

  return {
    ...chartOptions,
    plotOptions: {
      ...chartOptions.plotOptions,
      series: {
        ...chartOptions.plotOptions?.series,
        groupPadding: 0,
        pointWidth,
      },
    },
  }
}

export const enhanceChartOptions = (chartOptions) => {
  const optionsWithFormattedYAxis = formatYAxisLabels(chartOptions)

  const optionsWithPadding = setPlotPadding(optionsWithFormattedYAxis)

  return optionsWithPadding
}


/**
 * Get default options for a specific chart type.
 */
const getDefaultOptions = (chartType: string) => {
  const types = {
    boxplot: boxplotOptions,
    scatter: scatterplotOptions,
    line: lineOptions,
    column: columnOptions,
    bar: barOptions,
    variwide: variwideOptions,
  }

  return {
    ...genericOptions,
    ...types[chartType],
  }
}

export function convertToHighcharts(inputObject: IChart) {
  switch (ChartTypes[inputObject.chartType]) {
    case 'boxplot':
      return createBoxplotChartOptions(inputObject)
    case 'line':
      return createLineChartOptions(inputObject)
    case 'column':
      return createColumnChartOptions(inputObject)
    case 'table':
      return createTableOptions(inputObject)
    case 'columnWithHighlights':
      return createColumnWithHighlightsChartOptions(inputObject)
    case 'stackedColumn':
      return createStackedColumnChartOptions(inputObject)
    default:
      return createTableOptions(inputObject)
  }
}

export function getDataTypeFromId(dataType: number): IDataType {
  return DATA_TYPES.get(dataType) ?? DATA_TYPES[0]
}

export function isPercentageDataType(dataType: number | null | undefined): boolean {
  if (!dataType) return false
  return (
    dataType === DataType.Percent0 ||
    dataType === DataType.Percent1 ||
    dataType === DataType.Percent2
  )
}

function formatLabel(value: number | null | undefined, dataTypeId: number): string {
  if (!value)
    return ''

  const dataType = getDataTypeFromId(dataTypeId)
  if (!dataType) {
    return value.toString()
  }

  let label = dataType.decimalPoints >= 0 ? value.toLocaleString(undefined, { minimumFractionDigits: dataType.decimalPoints, maximumFractionDigits: dataType.decimalPoints }) : value.toString()
  if (dataType.suffix !== '')
    label = `${label} ${dataType.suffix}`
  return label
}

const getPropertiesWithColumnsData = (data: IChart): Properties => {
  const newObj = {}
  const key = data.axisX.label
  const value = [...data.columns.map(c => c.name)]
  newObj[key] = value
  return { ...newObj, ...data.properties }
}

const getObjectWithoutEmptyArrayProperties = (obj: object): object => {
  const htmlEmptyString = '&nbsp;'
  const getPropertieWithoutEmptyStrings = (property: string[]) => [...property.map(v => v === '' ? htmlEmptyString : v)]

  for (const key in obj) {
    const propIsEmpty = Array.isArray(obj[key]) && (obj[key]?.length === 0 || obj[key]?.every(e => e === ''))
    if (propIsEmpty) {
      delete obj[key]
    }
    else {
      obj[key] = getPropertieWithoutEmptyStrings(obj[key])
    }
  }
  return obj
}

const createStackedColumnChartOptions = (data: IChart) => {
  // Extracting Percent or not from properties?
  const isPercent = true

  const rows = data.rows
  const columns = data.columns

  const seriesNames = rows.map(row => row.name)
  const columnNames = columns.map(col => col.name)
  const seriesData: SeriesDataType[][] = seriesNames.map(() => [])
  const displayPropertiesAsLabels = Object.keys(data.properties).length > 0
  if (displayPropertiesAsLabels) {
    data.properties = getPropertiesWithColumnsData(data)
    data.properties = getObjectWithoutEmptyArrayProperties(data.properties) as Properties
  }

  for (let i = 0; i < rows.length; i++) {
    const rowData = rows[i].cells
    for (let j = 0; j < columns.length; j++) {
      const cell = rowData[j]
      const val: number = parseFloat(cell.value) // why the slice?? .slice(0, -4)
      seriesData[i].push(val)
    }
  }

  const series = seriesData.map((data, i) => ({
    type: 'column',
    marker: {
      symbol: 'circle',
      fillColor: '',
    },
    name: seriesNames[i],
    data,
  }))

  const plotOptions = {
    column: {
      stacking: isPercent ? 'percent' : 'normal',
      dataLabels: {
        enabled: isPercent,
        inside: true,
        verticalAlign: 'middle',
        style: {
          color: '#FFFFFF',
          textOutline: 'none',
          fontSize: '12px',
          fontFamily: 'Barlow-Light, Arial, sans-serif',
        },
        formatter: function (this: any) {
          if (isPercent) {
            return this.y === 0
              ? null
              : Highcharts.numberFormat(this.percentage, 0) + '%'
          }
        },
      },
    },
  }

  const tooltipOptions = {
    formatter: function (this: Highcharts.TooltipFormatterContextObject) {
      let total = 0
      this.points?.forEach(function (point) {
        total += point?.y ?? 0
      })
      return (
        '<b>' +
        this.x +
        '</b><br/>' +
        this.points
          ?.map(function (point) {
            return (
              point.series.name +
              ': ' +
              '' +
              Highcharts.numberFormat((point?.y ?? 0 / total) * 100, 1) +
              '%'
            )
          })
          .join('<br/>')
      )
    },
    shared: true,
  }

  const getXAxisLabels = (data: IChart) => {
    const { properties } = data

    const formatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
      const columnIndex = this.axis.categories.indexOf(this.value.toString())
      const rowYearIndex = 0
      let rowIndex = 0
      let htmlXAxisColumnLabels = ''
      for (const key in properties) {
        htmlXAxisColumnLabels = `${htmlXAxisColumnLabels}
          <div style="text-align: center;">
            <div style="margin-top: 10px;">
              ${columnIndex === 0 ? `
                <div style="display: inline-block; position: absolute; left: -10px; transform: translateX(-100%); font-size: 13px;">
                  ${rowIndex == rowYearIndex ? '' : `${capitalizeFirstLetter(key)}:`}
                </div>`
            :
            ''} 
              <div> ${properties[key][columnIndex]}</div>
            </div>
          </div>
          `
        rowIndex++
      }
      return htmlXAxisColumnLabels
    }
    return {
      formatter,
      useHTML: true,
    }
  }

  const chartOptions = {
    chart: {
      type: 'column',
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: columnNames,
      labels: displayPropertiesAsLabels ? getXAxisLabels(data) : null,
      title: {
        text: displayPropertiesAsLabels ? null : data?.axisX?.label,
      },
    },
    yAxis: {
      title: {
        text: data?.axisY?.label,
      },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          if (typeof this.value === 'number') {
            return formatLabel(this.value, data.axisY.valueType)
          }
          return this.value.toString()
        },
      },
    },
    series: youFirst(series),
    plotOptions: plotOptions,
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    tooltip: tooltipOptions,
  }

  return chartOptions
}

const parseCellValue = (cellValue: string): number | string => {
  const parsedValue = parseFloat(cellValue)
  return isNaN(parsedValue) ? cellValue : parsedValue
}

const youFirst = (series: any[]) => {
  return series.sort((a, b) => {
    if (a.name === 'You') return -1
    if (b.name === 'You') return 1
    return 0
  })
}

/**
 * Create options for table chart.
 */
const createTableOptions = (data: IChart) => {
  const { rows, columns, axisX, axisY } = data

  const seriesNames = rows.map(row => row.name)
  const columnNames = columns.map(col => col.name)
  const seriesData: (number | string)[][] = seriesNames.map(() => [])

  rows.forEach((row, i) => {
    row.cells.forEach((cell, j) => {
      seriesData[i].push(parseCellValue(cell.value))
    })
  })

  const series = seriesData.map((data, i) => ({
    type: 'column',
    name: seriesNames[i],
    data,
  } as any))

  const chartOptions = {
    chart: { type: 'table' },
    title: { text: '' },
    xAxis: {
      categories: columnNames,
      title: { text: axisX?.label },
    },
    yAxis: {
      title: { text: axisY?.label },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          if (typeof this.value === 'number') {
            return formatLabel(this.value, data.axisY.valueType)
          }
          return this.value.toString()
        },
      },
    },
    series: youFirst(series),
    tooltip: {
      pointFormatter: function (this: Highcharts.Point) {
        // Use formatLabel with the stored data type from series
        const seriesOptions = this.series.options as any
        const dataType = seriesOptions.dataType || data.axisY.valueType
        return `<b>${formatLabel(this.y, dataType)}</b>`
      },
    },
    exporting: { enabled: false },
    credits: { enabled: false },
  }

  return chartOptions
}

/**
 * Create options for column chart.
 */
const createColumnChartOptions = (data: IChart) => {
  const hasMedian = data.properties?.median !== null
  const lineData = {
    name: 'Median',
    value: data.properties?.median ?? null,
    colour: 'black',
  }
  const { rows, columns, axisX, axisY } = data

  const seriesNames = rows.map(row => row.name)
  const columnNames = columns.map(col => col.name)
  const seriesData: number[][] = seriesNames.map(() => [])

  rows.forEach((row, i) => {
    row.cells.forEach((cell, j) => {
      seriesData[i].push(parseFloat(cell.value))
    })
  })

  const series: Highcharts.SeriesOptionsType[] = seriesData.map((data, i) => ({
    type: 'column',
    marker: { symbol: 'circle', fillColor: '' },
    name: seriesNames[i],
    data,
  } as any))

  // Add line series for Median
  if (hasMedian) {
    series.push({
      type: 'line',
      marker: { enabled: false, symbol: 'square', fillColor: lineData.colour },
      color: lineData.colour,
      name: lineData.name,
      data: columnNames.map(() => lineData.value),
    })
  }

  const chartOptions = {
    chart: { type: 'column' },
    title: { text: '' },
    xAxis: {
      categories: columnNames,
      title: { text: axisX?.label },
    },
    yAxis: {
      title: { text: axisY?.label },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          if (typeof this.value === 'number') {
            return formatLabel(this.value, axisY.valueType)
          }
          return this.value.toString()
        },
      },
    },
    plotOptions: {
      line: {
        enableMouseTracking: false,
        dataLabels: {
          enabled: true,
          formatter: function (this: Highcharts.PointLabelObject) {
            return this.point.index === 0 ? this.y?.toFixed(2) : null // Show data label only for the first point
          },
        },
      },
    },
    series: youFirst(series),
    tooltip: {
      pointFormatter: function (this: Highcharts.Point) {
        // Use formatLabel with the stored data type from series
        const seriesOptions = this.series.options as any
        const dataType = seriesOptions.dataType || data.axisY.valueType
        return `<b>${formatLabel(this.y, dataType)}</b>`
      },
    },
    exporting: { enabled: false },
    credits: { enabled: false },
    legend: {
      enabled: true,
      itemHoverStyle: { color: 'initial' },
      itemHiddenStyle: { color: 'initial' },
    },
  }

  return chartOptions
}

/**
 * Create options for column chart with highlights.
 */
const createColumnWithHighlightsChartOptions = (data: IChart) => {
  const {
    properties: { groups, median, showXAxis = false },
    rows,
    columns,
    axisX,
    axisY,
  } = data

  const subCategories = groups.map((group) => ({
    label: group.label,
    ids: group.colIds,
    color: group.colorHint === 'You' ? YOU_COLOR : GROUP_COLOR,
  }))

  const hasMedian = median !== null

  const seriesNames = rows.map((row) => row.name)
  const columnNames = columns.map((col) => col.name)
  const seriesData = rows.map((row) => row.cells.map((cell) => parseFloat(cell.value)))

  const series: Highcharts.SeriesOptionsType[] = seriesData.map((data, i) => ({
    type: 'column',
    marker: { symbol: 'square' },
    color: GROUP_COLOR,
    name: seriesNames[i],
    data,
  } as any))

  const capYAxisToMaxValue = columns.every((col) => isPercentageDataType(col.dataType))

  const chartOptions: ChartOptions = {
    chart: { type: 'column' },
    title: { text: '' },
    xAxis: {
      categories: columnNames,
      title: { text: axisX?.label },
      labels: { enabled: showXAxis },
    },
    yAxis: {
      title: { text: axisY?.label },
      endOnTick: !capYAxisToMaxValue,
      labels: {
        enabled: true,
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          if (typeof this.value === 'number') {
            return formatLabel(this.value, axisY.valueType)
          }
          return this.value.toString()
        },
      },
    },
    plotOptions: {
      series: {
        states: { inactive: { opacity: 1 } },
        events: { legendItemClick: () => false },
      },
    },
    legend: { symbolRadius: 0 },
    series,
    tooltip: {
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        const seriesOptions = this.series.options as any
        const dataType = seriesOptions.dataType || axisY.valueType
        const tooltipLabel = showXAxis ? `<tspan style="font-size: 0.8em">${this.x}</tspan><br><b>${formatLabel(this.y, dataType)}</b>` : `<b>${formatLabel(this.y, dataType)}</b>`
        return tooltipLabel
      },
    },
    exporting: { enabled: false },
    credits: { enabled: false },
  }

  subCategories.forEach((subCat) => {
    series.push({
      type: 'column',
      marker: { symbol: 'square' },
      color: subCat.color,
      name: subCat.label,
      data: [],
    })

    subCat.ids.forEach((id) => {
      if (id >= chartOptions.series[0].data.length) return

      const value = chartOptions.series[0].data[id]
      chartOptions.series[0].data[id] = {
        name: subCat.label,
        y: value,
        color: subCat.color,
      }
    })
  })

  if (hasMedian) {
    series.push({
      type: 'line',
      name: 'Median',
      data: columnNames.map(() => median),
      marker: { enabled: false },
      color: LINE_COLOR,
    })
  }

  chartOptions.series = youFirst(chartOptions.series)
  return chartOptions
}

/**
 * Create options for line chart.
 */
const createLineChartOptions = (data: IChart) => {
  const { rows, columns, axisX, axisY, properties } = data

  const isPercent = isPercentageDataType(properties.dataType)

  const seriesNames = rows.map((row) => row.name) // e.g., 'You', 'Global'
  const columnNames = columns.map((column) => column.name) // e.g., Years

  const seriesData: number[][] = rows.map(() => [])

  rows.forEach((row, i) => {
    row.cells.forEach((cell) => {
      const cellDataType = getDataTypeFromId(cell.dataType)
      let value = parseFloat(cell.value)

      if (cellDataType?.decimalPoints >= 0) {
        value = parseFloat(value.toFixed(cellDataType.decimalPoints))
      }

      seriesData[i].push(value)
    })
  })

  const chartOptions: ChartOptions = {
    chart: { type: 'line' },
    title: { text: '' },
    xAxis: {
      categories: columnNames,
      title: { text: axisX?.label },
    },
    yAxis: {
      title: { text: axisY?.label },
      min: isPercent ? properties.minYAxis : undefined,
      max: isPercent ? properties.maxYAxis : undefined,
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          if (typeof this.value === 'number') {
            return formatLabel(this.value, axisY.valueType)
          }
          return this.value.toString()
        },
      },
    },
    series: youFirst(seriesData.map((data, i) => ({
      name: seriesNames[i],
      type: 'line',
      data,
      showInLegend: !(seriesNames[i] === 'You' && data.every((val) => isNaN(val))),
      color: seriesNames[i] === 'You' ? YOU_COLOR : GROUP_COLOR,
    }))),
    tooltip: {
      pointFormatter: function (this: Highcharts.Point) {
        // Use formatLabel with the stored data type from series
        const seriesOptions = this.series.options as any
        const dataType = seriesOptions.dataType || data.axisY.valueType
        return `<b>${formatLabel(this.y, dataType)}</b>`
      },
    },
    exporting: { enabled: false },
    credits: { enabled: false },
  }

  return chartOptions
}

/**
 * Create options for boxplot chart.
 */
const createBoxplotChartOptions = (data: IChart) => {
  const { rows, columns, axisY } = data
  const categories = rows.map(row => row.name)

  const boxplotData = categories.map(() => ({}))
  const scatterData: Record<string, Highcharts.SeriesScatterOptions> = {}

  rows.forEach((row, rowIndex) => {
    row.cells.forEach((cell, cellIndex) => {
      const boxplotProperty =
        columnMappings[columns[cellIndex].properties.boxplot].highchartsProperty

      const seriesDisplayName = columns[cellIndex].name
      const seriesOptions = dataProperties[seriesDisplayName]
      if (boxplotProperty === 'y') {
        if (!seriesOptions.displayChart) return
        if (!scatterData[seriesDisplayName]) {
          scatterData[seriesDisplayName] = {
            name: seriesDisplayName,
            data: [],
            ...seriesOptions,
          }
        }
        scatterData[seriesDisplayName]?.data?.push([
          rowIndex,
          Number(cell.value) * 100,
        ])
      } else {
        if (
          cell.value === '' ||
          cell.value === null ||
          cell.value === undefined
        )
          return // Skip this value if it is not set
        boxplotData[rowIndex][boxplotProperty] = Number(cell.value) * 100
      }
    })
  })

  const chartOptions = {
    chart: { type: 'boxplot' },
    title: { text: '' },
    subtitle: { text: '' },
    xAxis: { categories },
    yAxis: {
      title: { text: axisY?.label },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          if (typeof this.value === 'number') {
            return formatLabel(this.value, axisY.valueType)
          }
          return this.value.toString()
        },
      },
    },
    tooltip: {
      formatter: function (this: Highcharts.Point) {
        const format = dataProperties[this.series.name]?.format
        const displayName = datapointDisplayNames[this.series.name]

        if (format === 'percent2') {
          return `${displayName || this.series.name}: ${this.y?.toFixed(2)}%`
        } else {
          return `${displayName || this.series.name}: ${this.y}`
        }
      },
    },
    series: youFirst([
      {
        name: 'Boxplot Data',
        type: 'boxplot',
        data: boxplotData,
      },
      ...(Object.keys(scatterData).map(seriesName => ({
        name: seriesName,
        type: 'scatter',
        data: scatterData[seriesName].data,
      })) as Highcharts.SeriesOptionsType[]),
    ]),
    exporting: { enabled: false },
    credits: { enabled: false },
  }

  return chartOptions
}

const High = 'BoxPlot_p90'
const Q3 = 'BoxPlot_p75'
const Median = 'BoxPlot_p50'
const Q1 = 'BoxPlot_p25'
const Low = 'BoxPlot_p10'

const columnMappings = {
  [High]: { highchartsProperty: 'high' },
  [Q3]: { highchartsProperty: 'q3' },
  [Median]: { highchartsProperty: 'median' },
  [Q1]: { highchartsProperty: 'q1' },
  [Low]: { highchartsProperty: 'low' },
  Datapoint: { highchartsProperty: 'y' },
}

const dataProperties = {
  '90th percentile': {
    format: 'percent2',
    displayChart: true,
    displayDownload: true,
  },
  '75th percentile': {
    format: 'percent2',
    displayChart: true,
    displayDownload: true,
  },
  median: { format: 'percent2', displayChart: true, displayDownload: true },
  '25th percentile': {
    format: 'percent2',
    displayChart: true,
    displayDownload: true,
  },
  '10th percentile': {
    format: 'percent2',
    displayChart: true,
    displayDownload: true,
  },
  average: {
    format: 'percent2',
    color: '#FF0000',
    marker: { symbol: 'circle', radius: 3 },
    displayChart: true,
    displayDownload: true,
  },
  count: { format: 'number0', displayChart: false, displayDownload: true },
  You: {
    format: 'percent2',
    color: '#FF00FF',
    marker: { symbol: 'square', radius: 3 },
    displayChart: true,
    displayDownload: true,
  },
  'You, last year': {
    format: 'percent2',
    color: '#FFFF00',
    marker: { symbol: 'square', radius: 2 },
    displayChart: false,
    displayDownload: true,
  },
}

const p90 = 'p_90'
const p75 = 'p_75'
const p50 = 'p_50'
const p25 = 'p_25'
const p10 = 'p_10'

const datapointDisplayNames = {
  [p90]: '90th percentile',
  [p75]: '75th percentile',
  [p50]: 'median',
  [p25]: '25th percentile',
  [p10]: '10th percentile',
  Avg: 'average',
  Count: 'count',
  YOU: 'You',
  You2: 'You, last year',
}